/**
 * Science Video Carousel
 *
 * Handles per-instance isolation, Swiper slider, IntersectionObserver-based
 * autoplay management, and arrow visibility for each carousel section.
 *
 * Loaded deferred from sections/science-video-carousel.liquid.
 * Section IDs are queued in window.__svcQueue by inline scripts before
 * this file executes.
 */

(function () {
  'use strict';

  // Guard: only execute once even if multiple section instances add the
  // same script tag (Shopify CDN URL is identical for all instances).
  if (window.__svcLoaded) return;
  window.__svcLoaded = true;

  var reducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Per-instance initialiser ─────────────────────────────────────────────

  /**
   * Initialise one Science Video Carousel section.
   * @param {string} sectionId  The Shopify section.id string
   */
  function initInstance(sectionId) {
    var root = document.querySelector('[data-svc-id="' + sectionId + '"]');
    if (!root || root.dataset.svcReady) return; // skip if missing or already initialised
    root.dataset.svcReady = '1';

    var swiperEl  = root.querySelector('[data-svc-swiper]');
    var prevBtn   = root.querySelector('[data-svc-prev]');
    var nextBtn   = root.querySelector('[data-svc-next]');
    var liveEl    = root.querySelector('[data-svc-live]');

    if (!swiperEl) return;

    var slides      = Array.prototype.slice.call(root.querySelectorAll('.svc__slide'));
    var totalSlides = slides.length;

    if (totalSlides === 0) return;

    // Hide next arrow too if only one slide
    if (totalSlides === 1 && nextBtn) {
      nextBtn.setAttribute('aria-hidden', 'true');
    }

    var activeIndex     = 0;
    var isSectionVisible = false;
    var swiper          = null;

    // ── Helpers ────────────────────────────────────────────────────────────

    /**
     * Return the currently visible <video> element in a slide.
     * CSS shows either .svc__video--desktop or .svc__video--mobile
     * depending on viewport width. We check computed display style
     * to find whichever is currently rendered.
     */
    function getVisibleVideo(slideEl) {
      var videos = slideEl.querySelectorAll('.svc__video');
      for (var i = 0; i < videos.length; i++) {
        if (window.getComputedStyle(videos[i]).display !== 'none') {
          return videos[i];
        }
      }
      return null;
    }

    /** Pause every video in every slide. */
    function pauseAll() {
      slides.forEach(function (slide) {
        slide.querySelectorAll('.svc__video').forEach(function (v) {
          if (!v.paused) v.pause();
        });
      });
    }

    /**
     * Restart and play the visible video in slide at index idx.
     * Respects prefers-reduced-motion: does nothing if set.
     */
    function playSlide(idx) {
      if (reducedMotion) return;
      pauseAll();

      var slideEl = slides[idx];
      if (!slideEl) return;

      var video = getVisibleVideo(slideEl);
      if (!video) return;

      video.currentTime = 0;
      var promise = video.play();
      if (promise !== undefined) {
        // Silently swallow autoplay rejections (policy block, non-muted, etc.)
        promise.catch(function () {});
      }
    }

    /**
     * Update prev/next arrow visibility.
     * First slide  → hide prev
     * Last slide   → hide next
     * Middle slides → show both
     */
    function updateArrows(idx) {
      if (prevBtn) {
        prevBtn.setAttribute('aria-hidden', idx === 0 ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.setAttribute('aria-hidden', idx === totalSlides - 1 ? 'true' : 'false');
      }
    }

    /** Announce current panel to screen readers via aria-live region. */
    function updateLive(idx) {
      if (liveEl) {
        liveEl.textContent = 'Panel ' + (idx + 1) + ' of ' + totalSlides;
      }
    }

    /**
     * Central handler called whenever the active slide changes.
     * Updates arrows, live region, and (if visible) plays the new slide.
     */
    function onSlideChange(idx) {
      activeIndex = idx;
      updateArrows(idx);
      updateLive(idx);
      if (isSectionVisible) {
        playSlide(idx);
      }
    }

    // ── Swiper ─────────────────────────────────────────────────────────────

    swiper = new Swiper(swiperEl, {
      slidesPerView : 1.15, // mobile: ~87% active, ~13% peek
      spaceBetween  : 16,
      speed         : 420,
      loop                : false,
      centeredSlides      : true,
      centeredSlidesBounds: true,
      grabCursor          : true,
      breakpoints   : {
        769: {
          slidesPerView : 1.12, // desktop: slightly less peek
          spaceBetween  : 24,
        },
      },
      // Keyboard arrow keys navigate between slides
      keyboard      : { enabled: true },
      a11y          : {
        prevSlideMessage : 'Previous panel',
        nextSlideMessage : 'Next panel',
      },
      on: {
        // slideChange fires when the transition begins;
        // slideChangeTransitionEnd fires when animation completes.
        // We use TransitionEnd so video swap is invisible.
        slideChangeTransitionEnd: function (sw) {
          onSlideChange(sw.activeIndex);
        },
      },
    });

    // ── Custom arrow buttons ───────────────────────────────────────────────
    // Swiper's built-in navigation is NOT used so we can control visibility
    // with aria-hidden instead of just display:none (better for a11y).

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        swiper.slidePrev();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        swiper.slideNext();
      });
    }

    // ── Click / tap to restart ─────────────────────────────────────────────

    slides.forEach(function (slide, idx) {
      var wrap = slide.querySelector('.svc__video-wrap');
      if (!wrap) return;
      wrap.addEventListener('click', function () {
        // Only restart if this slide is the currently active one
        if (idx !== activeIndex) return;
        var video = getVisibleVideo(slide);
        if (!video || reducedMotion) return;
        video.currentTime = 0;
        var p = video.play();
        if (p !== undefined) p.catch(function () {});
      });
    });

    // ── IntersectionObserver: play when in view, pause when out ───────────

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            isSectionVisible = entry.isIntersecting;
            if (entry.isIntersecting) {
              playSlide(activeIndex);
            } else {
              pauseAll();
            }
          });
        },
        { threshold: 0.2 } // fire when 20% of section is visible
      );
      observer.observe(root);
    } else {
      // Older browsers without IntersectionObserver: just autoplay
      isSectionVisible = true;
      playSlide(activeIndex);
    }

    // ── Initial state ──────────────────────────────────────────────────────

    updateArrows(0);
    // Don't announce panel on load — screen readers will read the section heading instead

    // ── Theme Editor (Shopify design mode) support ─────────────────────────

    if (window.Shopify && window.Shopify.designMode) {
      // When the editor reloads this section, re-init arrows
      document.addEventListener('shopify:section:load', function (e) {
        if (e.detail && e.detail.sectionId === sectionId) {
          updateArrows(activeIndex);
        }
      });
      // When a block is selected in the editor, navigate to that slide
      document.addEventListener('shopify:block:select', function (e) {
        if (!e.target) return;
        var slide = e.target.closest('.svc__slide');
        if (!slide) return;
        var idx = slides.indexOf(slide);
        if (idx > -1) {
          swiper.slideTo(idx, 0); // 0 = instant, no animation in editor
          onSlideChange(idx);
        }
      });
    }
  }

  // ── Queue processor: wait for Swiper, then init all queued sections ──────

  function processQueue() {
    if (typeof Swiper === 'undefined') {
      // Swiper CDN script hasn't executed yet — retry
      setTimeout(processQueue, 80);
      return;
    }

    var queue = window.__svcQueue || [];
    queue.forEach(function (id) {
      initInstance(id);
    });

    // Expose for sections added after initial load (e.g. Theme Editor)
    window.__svcInitSection = initInstance;
  }

  // ── Theme Editor: section added dynamically after initial page load ──────

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    var inner = e.target.querySelector('[data-svc-id]');
    if (!inner) return;
    var id = inner.getAttribute('data-svc-id');
    if (id && typeof Swiper !== 'undefined') {
      initInstance(id);
    }
  });

  // ── Boot ──────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processQueue);
  } else {
    processQueue();
  }

}());
